import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { HomeAssistantBar } from './assistant/home-bar.jsx';
import {
  Building2,
  ClipboardCheck,
  Construction,
  DoorOpen,
  ExternalLink,
  FileSignature,
  Gauge,
  KeyRound,
  Megaphone,
  MessageSquare,
  Percent,
  Receipt,
  ShieldX,
  UserRound,
  Wallet,
} from 'lucide-react';
import {
  canAccess,
  formatMoney,
  formatMonth,
  formatNumber,
  formatPercent,
  hasPermission,
  HOME_CONTACT_ROLE_LABELS,
  labelOf,
  ROLE_LABELS,
  toNumber,
} from '@blimo/shared';
import { portalQueries } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { Badge, Button, EmptyState, ErrorState, Skeleton, useBranding } from '@blimo/ui';
import { ContactDetails } from '../components/property-bits.jsx';
import { PageHeader } from '../components/ui/page-header.jsx';
import { Section } from '../components/ui/section.jsx';
import { KpiCard } from '../components/ui/kpi-card.jsx';

function greeting(date = new Date()) {
  const h = date.getHours();
  if (h < 11) return 'Guten Morgen';
  if (h < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/**
 * Tiles of GET /portal/home (docs/frontend/portal.md §2 Übersicht). The API only sends keys the user may see –
 * a tile is rendered only when its key is present (a missing key is not 0).
 */
const HOME_TILES = [
  { key: 'unread_announcements', label: 'Ungelesene Mitteilungen', icon: Megaphone, to: '/mitteilungen', link: 'Mitteilungen öffnen' },
  { key: 'unread_message_replies', label: 'Neue Antworten', icon: MessageSquare, to: '/nachrichten', link: 'Nachrichten öffnen' },
  { key: 'open_document_checks', label: 'Offene Belegprüfungen', icon: ClipboardCheck, to: '/belegpruefung', link: 'Belegprüfung öffnen' },
  { key: 'open_meter_readings', label: 'Offene Zähler', icon: Gauge, to: '/zaehlerablesung', link: 'Zählerablesung öffnen' },
  { key: 'upcoming_proxies', label: 'Anstehende Vollmachten', icon: FileSignature, to: '/vollmachten', link: 'Vollmachten öffnen' },
];

const KPI_GRID = 'grid grid-cols-2 gap-3 lg:grid-cols-4';

export function OverviewPage() {
  const api = useApi();
  const { data: me } = useMe();
  const properties = useQuery(portalQueries.properties(api));
  const home = useQuery(portalQueries.home(api));
  const { welcome_text: welcomeText } = useBranding();
  if (!me) return null;
  const firstName = me.name?.split(' ')[0] ?? '';
  const list = properties.data ?? [];
  const unitCount = list.reduce((sum, p) => sum + (p.my_units?.length ?? 0), 0);
  const tiles = HOME_TILES.filter((tile) => home.data?.tiles && Object.hasOwn(home.data.tiles, tile.key));
  const contacts = home.data?.contacts ?? [];
  const portalRoles = (me.roles ?? []).filter((r) => PORTAL_ROLES.includes(r));
  const canSeeProperties = canAccess(me, { roles: ['owner', 'investor'] });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}`}
        compactTitle="Übersicht"
        description={
          welcomeText ? (
            <span className="whitespace-pre-line" data-testid="branding-welcome">
              {welcomeText}
            </span>
          ) : (
            'Willkommen in Ihrem Kundenportal – hier sehen Sie, was bei Ihren Immobilien ansteht.'
          )
        }
      />

      <HomeAssistantBar me={me} />

      <HighlightedLinks />

      {home.error ? (
        <ErrorState compact error={home.error} onRetry={() => home.refetch()} />
      ) : (
        tiles.length > 0 && (
          <Section plain title="Ihre Aufgaben">
            <div className={KPI_GRID}>
              {tiles.map((tile) => (
                <KpiCard
                  key={tile.key}
                  label={tile.label}
                  icon={tile.icon}
                  value={formatNumber(toNumber(home.data.tiles[tile.key]) ?? 0)}
                  to={tile.to}
                  linkText={tile.link}
                />
              ))}
            </div>
          </Section>
        )
      )}

      {hasPermission(me, 'finance.view') && <FinanceKpis />}

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <Section plain title="Ihr Überblick">
          {properties.error ? (
            <ErrorState compact error={properties.error} onRetry={() => properties.refetch()} />
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <KpiCard
                label="Liegenschaften"
                icon={Building2}
                loading={properties.isPending}
                value={formatNumber(list.length)}
                to={canSeeProperties && list.length > 0 ? '/immobilien' : undefined}
                linkText="Zu meinen Immobilien"
              />
              <KpiCard label="Einheiten" icon={DoorOpen} loading={properties.isPending} value={formatNumber(unitCount)} hint="Ihnen zugeordnet" />
              <KpiCard label="Ihre Rollen" icon={UserRound} className="col-span-2">
                {portalRoles.length ? (
                  <span className="flex min-h-8 flex-wrap items-center gap-1.5">
                    {portalRoles.map((role) => (
                      <Badge key={role} variant="secondary">
                        {ROLE_LABELS[role] ?? role}
                      </Badge>
                    ))}
                  </span>
                ) : (
                  <span className="text-[0.8125rem] leading-8 text-muted-foreground">Noch keine Daten</span>
                )}
              </KpiCard>
            </div>
          )}
        </Section>

        <Section
          title={contacts.length > 1 ? 'Ihre Ansprechpartner' : 'Ihr Ansprechpartner'}
          description="Ihre zuständige Verwaltung"
          flush={contacts.length > 0}
        >
          {home.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : contacts.length === 0 ? (
            <EmptyState
              compact
              icon={UserRound}
              title="Noch kein Ansprechpartner hinterlegt"
              description="Sobald Ihrer Liegenschaft ein Ansprechpartner zugeordnet ist, finden Sie hier Telefon und E-Mail."
              className="border-0 p-2"
            />
          ) : (
            <ul className="flex flex-col divide-y" aria-label="Ansprechpartner">
              {contacts.map((contact) => (
                <li key={`${contact.role}-${contact.name}-${contact.email}-${contact.phone}`} className="px-4 py-3">
                  <ContactDetails contact={contact} role={labelOf(HOME_CONTACT_ROLE_LABELS, contact.role)} />
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>
    </div>
  );
}

const PORTAL_ROLES = ['owner', 'investor', 'auditor', 'meter-reader'];

/**
 * Investor KPIs from GET /portal/finance/summary (values computed by the API).
 * 403 (no finance.view after all) → the section is not shown.
 */
function FinanceKpis() {
  const api = useApi();
  const summary = useQuery(portalQueries.financeSummary(api));
  if (summary.error?.status === 403) return null;
  const s = summary.data;
  const openItems = toNumber(s?.open_items_total);
  return (
    <Section plain title="Kennzahlen">
      {summary.error ? (
        <ErrorState compact error={summary.error} onRetry={() => summary.refetch()} />
      ) : (
        <div className={KPI_GRID}>
          <KpiCard
            label="Ø-Rendite"
            icon={Percent}
            loading={summary.isPending}
            value={s?.average_yield == null ? null : formatPercent(s.average_yield)}
            emptyText="Kein Kaufpreis hinterlegt"
            hint={s?.average_yield_period ? `Bruttomietrendite, Stand ${formatMonth(s.average_yield_period)}` : 'Bruttomietrendite'}
            to="/entwicklung"
            linkText="Entwicklung öffnen"
          />
          <KpiCard
            label="Vermietete Einheiten"
            icon={KeyRound}
            loading={summary.isPending}
            value={s ? `${formatNumber(s.rented_units)} / ${formatNumber(s.units)}` : null}
            hint={s ? `in ${formatNumber(s.properties)} ${s.properties === 1 ? 'Liegenschaft' : 'Liegenschaften'}` : undefined}
          />
          <KpiCard
            label="Offene Posten"
            icon={Wallet}
            loading={summary.isPending}
            value={
              s ? <span className={openItems !== null && openItems < 0 ? 'text-destructive' : undefined}>{formatMoney(openItems, { sign: true })}</span> : null
            }
            hint="Summe der Mieterkonten"
            to="/offene-posten"
            linkText="Details anzeigen"
          />
          <KpiCard
            label="Kosten"
            icon={Receipt}
            loading={summary.isPending}
            value={s ? formatMoney(s.costs_last_6_months) : null}
            hint="letzte 6 Monate"
            to="/kosten"
            linkText="Kosten im Detail"
          />
        </div>
      )}
    </Section>
  );
}

/** @param {{ item: import('../nav.js').PortalNavItem }} props */
export function PlaceholderPage({ item }) {
  const { data: me } = useMe();
  if (!canAccess(me, item.access)) return <ForbiddenPage />;
  const Icon = item.icon ?? Construction;
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={item.label} />
      <EmptyState icon={Icon} title="Wird in Phase D/E umgesetzt" description="Dieser Bereich ist vorbereitet. Inhalte folgen in Kürze." />
    </div>
  );
}

export function ForbiddenPage() {
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Kein Zugriff" />
      <EmptyState
        icon={ShieldX}
        title="Dieser Bereich ist für Ihr Konto nicht freigeschaltet"
        description="Wenn Sie Zugriff benötigen, wenden Sie sich bitte an Ihre Verwaltung."
        action={
          <Button asChild variant="outline">
            <Link to="/">Zur Übersicht</Link>
          </Button>
        }
      />
    </div>
  );
}

export function NotFoundPage() {
  return (
    <div className="mx-auto flex min-h-dvh max-w-xl flex-col gap-4 bg-(--shell) px-4 pt-[calc(3rem+env(safe-area-inset-top))]">
      <PageHeader title="Seite nicht gefunden" />
      <EmptyState
        title="Diese Seite gibt es nicht"
        description="Prüfen Sie die Adresse oder kehren Sie zur Übersicht zurück."
        action={
          <Button asChild variant="outline">
            <Link to="/">Zur Übersicht</Link>
          </Button>
        }
      />
    </div>
  );
}

/**
 * Hervorgehobene Links der Verwaltung (F-1005) als Schaltflächen – z. B. ein Sonderzugang nur für einzelne Benutzer.
 * Welche Links ein Benutzer sieht, entscheidet die API (Zielgruppe); ohne Links wird nichts angezeigt.
 */
export function HighlightedLinks() {
  const api = useApi();
  const query = useQuery(portalQueries.highlightedLinks(api));
  const links = query.data ?? [];
  if (links.length === 0) return null;
  return (
    <section aria-label="Wichtige Links" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {links.map((link) => (
        <a
          key={link.id}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center justify-between gap-3 rounded-xl border bg-card p-4 shadow-xs transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-2 focus-visible:outline-primary"
        >
          <span className="flex min-w-0 flex-col">
            <span className="font-medium text-foreground">{link.name}</span>
            {link.description && <span className="line-clamp-2 text-sm text-muted-foreground">{link.description}</span>}
          </span>
          <ExternalLink className="size-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="sr-only">(öffnet in neuem Tab)</span>
        </a>
      ))}
    </section>
  );
}
